/**
 * Google Identity Services (GSI) loader + renderer.
 *
 * Uso:
 *   const ref = useRef<HTMLDivElement>(null);
 *   useEffect(() => {
 *     renderGoogleButton(ref.current!, async (credential) => {
 *       await api.googleLogin({ IdToken: credential });
 *     });
 *   }, []);
 */

const SCRIPT_ID = 'google-gsi-script';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

let _loadPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (!CLIENT_ID) return Promise.reject(new Error('Google client id nao configurado'));
  if (_loadPromise) return _loadPromise;

  _loadPromise = new Promise<void>((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('GSI load failed')));
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('GSI load failed'));
    document.head.appendChild(s);
  });
  return _loadPromise;
}

// GSI exige initialize() uma unica vez por client_id. Re-init causa warning
// "initialize() is called multiple times" e o callback novo pode nao funcionar.
// Guardamos o estado + o callback atual (que pode mudar entre renders).
let _initialized = false;
let _currentCallback: ((credential: string) => void) | null = null;

/**
 * Renderiza o botao oficial do Google em `parent`. `onCredential` recebe o JWT
 * a ser enviado pro backend (POST /Account/googleLogin {IdToken}).
 */
export async function renderGoogleButton(
  parent: HTMLElement,
  onCredential: (credential: string) => void,
  opts: { text?: 'signin_with' | 'signup_with' | 'continue_with'; width?: number } = {},
): Promise<void> {
  await loadScript();
  if (!window.google?.accounts?.id) throw new Error('GSI nao disponivel');

  _currentCallback = onCredential;

  if (!_initialized) {
    window.google.accounts.id.initialize({
      client_id: CLIENT_ID,
      // Repassa pro callback ATUAL — assim re-renders nao precisam re-initialize.
      callback: (resp) => _currentCallback?.(resp.credential),
      auto_select: false,
      cancel_on_tap_outside: true,
    });
    _initialized = true;
  }

  parent.innerHTML = ''; // clear before re-render
  window.google.accounts.id.renderButton(parent, {
    type: 'standard',
    theme: 'filled_black',
    size: 'large',
    text: opts.text ?? 'continue_with',
    shape: 'rectangular',
    width: opts.width ?? 320,
    locale: 'pt-BR',
  });
}

export const isGoogleEnabled = (): boolean => Boolean(CLIENT_ID);
